<template>
	<v-row class="module">
		<v-col class="primary--text">
			<!-- Title -->
			<h1 class="mb-2">{{ module.title }}</h1>
			<!-- Subtitle -->
			<h2 :class="{ 'mb-10': $vuetify.breakpoint.smAndUp, 'mb-8': $vuetify.breakpoint.xsOnly }">{{ module.subTitle }}</h2>
			<!-- Search field -->
			<v-row no-gutters style="max-width: 600px; position: relative;" class="elevation-1">
				<!-- Mobile search hitbox -->
				<div class="search__hitbox d-md-none" style="" @click.stop="openMobileSearchDialog"></div>

				<v-col>
					<search-location v-if="$vuetify.breakpoint.mdAndUp" v-model="location" @selected="searchExecute" prepend-inner-icon="place" style="width: 100%;" :placeholder="module.searchText" />
					<v-combobox
						v-else
						v-model="location"
						ref="input"
						v-mutate.attr="fixInaccessibleName"
						item-text="name"
						:placeholder="module.searchText"
						no-data-text="Ingen treff"
						append-icon="search"
						height="50"
						prepend-inner-icon="place"
						solo
						full-width
						flat
						hide-details
						depressed
						clearable
					/>
				</v-col>
				<!-- Temp removed to see if need this button. Could be replace by a "Bruk min lokasjon" button? -->
				<!-- <v-col v-if="$vuetify.breakpoint.smAndUp" :cols="$vuetify.breakpoint.xsOnly ? 12 : 'auto'">
					<v-btn
						@click="searchExecute(location)"
						color="primary"
						height="56"
						:block="$vuetify.breakpoint.xsOnly"
						depressed
						class="ma-0"
						:class="[$vuetify.breakpoint.xsOnly && 'mt-4']"
						:disabled="!location"
					>
						{{ module.searchButtonText }}
					</v-btn>
				</v-col> -->
			</v-row>
		</v-col>

		<!-- Image sequence -->
		<v-col cols="5" v-if="$vuetify.breakpoint.mdAndUp" align-self="center" class="d-flex align-center" style="position: relative;">
			<cover-image-sequence :images="module.images" :settings="module.settings" />
		</v-col>

		<!-- Custom fullscreen search dialog for mobile -->
		<transition name="search__dialog--fade">
			<section v-show="showMobileSearchDialog" class="search__dialog" :style="`margin-top: ${$root.headerHeight(!$root.hasDenseHeader)}px`">
				<v-card class="search__dialog__content">
					<v-row style="position: relative" class="mx-0">
						<v-col cols="auto" class="px-0" style="margin-top: 10px">
							<v-btn text color="primary" icon @click="closeMobileSearchDialog" class="px-2 ml-4">
								<v-icon>arrow_back</v-icon>
							</v-btn>
						</v-col>
						<v-col class="px-0">
							<search-location v-model="location" @selected="searchExecute" inside-dialog />
						</v-col>
					</v-row>
				</v-card>
			</section>
		</transition>
	</v-row>
</template>

<script>
import { CoverImageSequence, SearchLocation } from '@/views/frontpage/components';
import { searchLocationMixins } from '@/views/frontpage/components/searchLocation.mixins';

export default {
	name: 'search-and-cover-image-module',
	mixins: [searchLocationMixins],
	components: { CoverImageSequence, SearchLocation },
	props: ['module'],
	data: () => ({
		location: null,
		showMobileSearchDialog: false
	}),
	methods: {
		openMobileSearchDialog() {
			this.showMobileSearchDialog = true;
			this.focusAndOpenKeyboard(document.querySelector('.search-location input'), 500);
		},

		closeMobileSearchDialog() {
			const el = document.querySelector('.search-location input');
			if (el) el.blur();
			this.showMobileSearchDialog = false;
		},

		focusAndOpenKeyboard(el, timeout = 100) {
			if (el) {
				// Align temp input element approximately where the input element is so the cursor doesn't jump around
				const $temp = document.createElement('input');
				Object.assign($temp.style, {
					position: 'absolute',
					top: `${el.offsetTop + 7}px`,
					left: `${el.offsetLeft}px`,
					height: 0,
					opacity: 0
				});
				document.body.appendChild($temp);
				$temp.focus();

				// The keyboard is open. Now do a delayed focus on the target element
				setTimeout(function() {
					el.focus();
					el.click();

					// Remove the temp element
					document.body.removeChild($temp);
				}, timeout);
			}
		},

		/**
		 * The `clear` action for the v-combobox has an inaccessible name. This is a temporary fix until Vuetify add the missing aria-label to it.
		 * https://web.dev/button-name/
		 */
		fixInaccessibleName() {
			this.$nextTick(() => {
				if (this.$refs.input) {
					this.$refs.input.$el.querySelector('.v-input__append-inner .v-icon').setAttribute('aria-label', 'Tøm søkefelt');
				}
			});
		}
	},
	created() {
		// The fake search field is missing associated label (inaccessible): https://www.w3.org/TR/WCAG20-TECHS/ARIA14.html
		this.$nextTick(() => {
			if (this.$vuetify.breakpoint.smAndDown && this.$refs.input) {
				this.$refs.input.$el.querySelector('input').setAttribute('aria-label', 'Søkefelt');
			}
		});
	}
};
</script>

<style lang="scss" scoped>
@import '~@/styles/variables';

.module {
	h1 {
		font-size: 72px;
		font-weight: 400;
		line-height: 1.2 !important;
		max-width: 15ch;

		@media #{map-get($display-breakpoints, 'xs-only')} {
			font-size: 10vw;
		}
	}

	h2 {
		font-family: 'niveau-grotesk' !important;
		font-size: 36px;
		font-weight: 400;
		line-height: 1.2 !important;

		@media #{map-get($display-breakpoints, 'xs-only')} {
			font-size: 6vw;
		}
	}
}

.search {
	&__dialog {
		background-color: white;
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
		margin: 0;
		position: fixed;
		overflow-y: auto;
		z-index: 202;

		&__content {
			background-color: white;
			box-shadow: unset;
		}

		&--fade {
			&-enter-active {
				animation: animatezoomin 0.2s;
				@keyframes animatezoomin {
					from {
						transform: scale(0.5);
					}
					to {
						transform: scale(1);
					}
				}
			}

			&-leave-active {
				animation: animatezoomout 0.2s;
				@keyframes animatezoomout {
					from {
						transform: scale(1);
					}
					to {
						transform: scale(0);
					}
				}
			}
		}
	}

	&__hitbox {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		height: 56px;
		z-index: 1;
	}
}
</style>
